import * as THREE from 'three';

export const UI_FONT = '"Fredoka", "Baloo 2", "Trebuchet MS", system-ui, sans-serif';

export interface SignStyle {
  width?: number;
  height?: number;
  background?: string;
  border?: string;
  color?: string;
  fontSize?: number;
}

/** Draws a rounded wooden-sign style label onto a canvas texture. */
export function drawSign(canvas: HTMLCanvasElement, text: string, style: SignStyle = {}): void {
  const width = (canvas.width = style.width ?? 512);
  const height = (canvas.height = style.height ?? 128);
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, width, height);
  const r = height * 0.28;
  ctx.fillStyle = style.background ?? '#f6d9a8';
  ctx.strokeStyle = style.border ?? '#9c6b45';
  ctx.lineWidth = height * 0.08;
  ctx.beginPath();
  ctx.roundRect(
    ctx.lineWidth / 2,
    ctx.lineWidth / 2,
    width - ctx.lineWidth,
    height - ctx.lineWidth,
    r,
  );
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = style.color ?? '#6b3f22';
  let fontSize = style.fontSize ?? height * 0.5;
  ctx.font = `600 ${fontSize}px ${UI_FONT}`;
  while (ctx.measureText(text).width > width * 0.86 && fontSize > 10) {
    fontSize -= 2;
    ctx.font = `600 ${fontSize}px ${UI_FONT}`;
  }
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, width / 2, height / 2 + fontSize * 0.04);
}

/** A sign texture that can be re-drawn (e.g. after fonts load or a rename). */
export class SignTexture {
  readonly canvas = document.createElement('canvas');
  readonly texture: THREE.CanvasTexture;
  private text = '';

  constructor(
    text: string,
    private readonly style: SignStyle = {},
  ) {
    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.anisotropy = 4;
    this.setText(text);
    // Re-draw once web fonts are ready so signs use the cute font.
    document.fonts?.ready.then(() => this.redraw()).catch(() => undefined);
  }

  setText(text: string): void {
    if (text === this.text) return;
    this.text = text;
    this.redraw();
  }

  private redraw(): void {
    drawSign(this.canvas, this.text, this.style);
    this.texture.needsUpdate = true;
  }
}
